import { Navigate } from "react-router-dom";
import {
  useFeatureFlag,
  useFeatureFlagsByCategory,
  useAllFeatureFlags,
} from "../hooks/query/featureFlagsQueries";

/**
 * FeatureGate - Component for feature flag control
 *
 * @param {string|string[]} flag - Flag(s) to check
 * @param {string} category - Flag category ("mainFeatures" | "settingsPage")
 * @param {React.ReactNode} children - Content to show when flag is enabled
 * @param {React.ReactNode} fallback - Content to show when flag is disabled (optional)
 * @param {string} redirectTo - Path to redirect when flag is disabled (optional)
 *
 * @example
 * // Single flag - hide content
 * <FeatureGate flag="autostack">
 *   <AutoStackButton />
 * </FeatureGate>
 *
 * @example
 * // Multiple flags - show if any is true
 * <FeatureGate flag={["buyForex", "sellForex"]}>
 *   <ForexSection />
 * </FeatureGate>
 *
 * @example
 * // With redirect
 * <FeatureGate flag="invest" redirectTo="/">
 *   <InvestDashboard />
 * </FeatureGate>
 *
 * @example
 * // With fallback
 * <FeatureGate flag="eightPay" fallback={<ComingSoon />}>
 *   <EightPayFeature />
 * </FeatureGate>
 */
export function FeatureGate({
  flag,
  category = "mainFeatures",
  children,
  fallback = null,
  redirectTo = null,
}) {
  const { data: featureFlags, isLoading } = useFeatureFlagsByCategory(category);

  if (isLoading) {
    return null;
  }

  // featureFlags is already transformed to simple booleans by the query hook
  // Use Map for safe property access
  const flagsMap = featureFlags
    ? new Map(Object.entries(featureFlags))
    : new Map();
  const isEnabled = Array.isArray(flag)
    ? flag.some((f) => flagsMap.get(f))
    : flagsMap.get(flag);

  if (!isEnabled) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return fallback;
  }

  return children;
}

/**
 * useFeatureEnabled - Hook for single flag check
 *
 * @param {string} category - Flag category
 * @param {string} key - Flag key
 * @returns {{ isEnabled: boolean, isLoading: boolean }}
 *
 * @example
 * const { isEnabled, isLoading } = useFeatureEnabled("mainFeatures", "autostack");
 * if (!isLoading && !isEnabled) return <Navigate to="/" />;
 */
export function useFeatureEnabled(category, key) {
  const { data: isEnabled, isLoading } = useFeatureFlag(category, key);
  return { isEnabled: !!isEnabled, isLoading };
}

/**
 * useFeatureAccess - Hook for multiple flag checks
 *
 * @param {string} category - Flag category
 * @param {string|string[]} flags - Flag(s) to check
 * @returns {{ isEnabled: boolean, isLoading: boolean, flags: object }}
 *
 * @example
 * // Single flag
 * const { isEnabled } = useFeatureAccess("mainFeatures", "autostack");
 *
 * @example
 * // Multiple flags - isEnabled=true if any is true
 * const { isEnabled, flags } = useFeatureAccess("mainFeatures", ["buyForex", "sellForex"]);
 * // flags = { buyForex: true, sellForex: false }
 */
export function useFeatureAccess(category, flags) {
  const { data: featureFlags, isLoading } = useFeatureFlagsByCategory(category);

  if (isLoading) {
    return { isEnabled: false, isLoading: true, flags: {} };
  }

  const flagArray = Array.isArray(flags) ? flags : [flags];

  // Use Map for safe property access
  const flagsMap = featureFlags
    ? new Map(Object.entries(featureFlags))
    : new Map();

  const flagResults = Object.fromEntries(
    flagArray.map((f) => [f, !!flagsMap.get(f)])
  );

  const isEnabled = flagArray.some((f) => flagsMap.get(f));

  return { isEnabled, isLoading: false, flags: flagResults };
}

/**
 * FeatureRoute - Route-level feature flag protection
 * For use in Root.jsx routing
 */
export function FeatureRoute({ flag, children, category = "mainFeatures" }) {
  return (
    <FeatureGate flag={flag} category={category} redirectTo="/">
      {children}
    </FeatureGate>
  );
}

// Re-export hooks for convenience
export { useFeatureFlag, useFeatureFlagsByCategory, useAllFeatureFlags };

export default FeatureGate;
